<template>
  <v-container fluid>
    <v-data-table
      :headers="headers"
      :items="datas"
      :items-per-page="itemsPerPage"
      :page.sync="page"
      item-key="id"
      :loading="loading"
      hide-default-footer
      hide-default-header
      class="card--shadow"
    >
      <template v-slot:top>
        <v-toolbar flat class="toolbar-custom">
          <v-toolbar-title class="toolbar-title">
            <v-icon class="mr-2" color="#aa00ff">mdi-pill</v-icon>
            DOTS
          </v-toolbar-title>
          <v-divider class="mx-3" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-btn
            @click="viewAll"
            class="action-btn mr-2"
            large
            rounded
          >
            <v-icon left>mdi-refresh</v-icon>
            View All
          </v-btn>
          <v-btn
            @click="showFilterModal = true"
            class="action-btn-primary mr-2"
            large
            rounded
          >
            <v-icon left>mdi-filter-variant</v-icon>
            Filter
          </v-btn>
          <v-btn
            to="/new-dots"
            class="action-btn-success"
            large
            rounded
          >
            <v-icon left>mdi-plus-circle-outline</v-icon>
            New Cross DOT
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:header="{ props: { headers } }">
        <thead class="header">
          <tr>
            <th
              v-for="header in headers"
              :key="header.text"
              class="header--text"
            >
              {{ header.text }}
            </th>
          </tr>
        </thead>
      </template>

      <template v-slot:item="{ index, item }">
        <tr>
          <td>
            {{ page > 1 ? (page - 1) * itemsPerPage + (index + 1) : index + 1 }}
          </td>
          <td>{{ item.patient && item.patient.name }}</td>
          <td>{{ item.patient.reg_unique_code }}</td>
          <td>{{ item.patient.sex == 1 ? "Male" : "Female" }}</td>
          <td>{{ item.dots_app_status == 1 ? "No" : "Yes" }}</td>
          <td>{{ item.volunteer && item.volunteer.name }}</td>
          <td>{{ item.reported_this_month == true ? "Yes" : "No" }}</td>
          <td>
            <span v-if="item.reported_this_month == true">{{
              item.reported_status
            }}</span>
          </td>
          <td>
            <v-btn
              @click="goToDetail(item)"
              icon
              color="orange darken-1"
              class="white--text"
              depressed
            >
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>

    <div v-if="!loading" class="text-center py-3">
      <v-pagination
        color="purple accent-4"
        :length="pageCount"
        v-model="page"
        :total-visible="7"
      ></v-pagination>
    </div>

    <dots-filter-box
      :dialog="showFilterModal"
      @cancel="showFilterModal = false"
    />
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import DotsFilterBox from "@/components/dots/DotsFilterBox.vue";

export default {
  components: {
    DotsFilterBox,
  },
  data: () => ({
    showFilterModal: false,
    page: 1,
    itemsPerPage: 30,
    headers: [
      { text: "No", value: "no" },
      { text: "Name", value: "name" },
      { text: "TB Code", value: "tb_code" },
      { text: "Sex", value: "sex" },
      { text: "Use App", value: "use_app" },
      { text: "Volunteer", value: "volunteer" },
      { text: "Reported", value: "reported" },
      { text: "Status", value: "status" },
      { text: "Actions", value: "actions" },
    ],
  }),
  methods: {
    goToDetail(item) {
      this.$router.push(`/dots-detail/${item.id}`);
    },

    viewAll() {
      this.page = 1;
      this.$router.push("/dots").catch(() => {});
    },

    hasActiveFilters(query = this.$route.query) {
      return !!(
        query.current_status !== undefined ||
        query.dots_app_status !== undefined ||
        query.reported_status !== undefined ||
        query.name ||
        (query.month && query.year)
      );
    },

    syncPageFromRoute() {
      this.page = this.$route.query.page
        ? parseInt(this.$route.query.page, 10)
        : 1;
    },

    getData() {
      const query = this.$route.query;
      const page = query.page || 1;

      if (!this.hasActiveFilters(query)) {
        this.$store.dispatch("DotsPatient/getAll", { page });
        return;
      }

      const params = { page };
      if (query.current_status !== undefined) {
        params.current_status = query.current_status;
      }
      if (query.name) {
        params.name = query.name;
      }
      if (query.dots_app_status !== undefined) {
        params.dots_app_status = query.dots_app_status;
      }
      if (query.reported_status !== undefined) {
        params.reported_status = query.reported_status;
      }
      if (query.month && query.year) {
        params.month = query.month;
        params.year = query.year;
      }

      this.$store.dispatch("DotsPatient/getByFilter", params);
    },
  },
  created() {
    this.syncPageFromRoute();
    this.getData();
  },
  watch: {
    page(newValue) {
      if (String(this.$route.query.page || 1) === String(newValue)) {
        return;
      }
      this.$router
        .push({
          path: "/dots",
          query: { ...this.$route.query, page: newValue },
        })
        .catch(() => {});
    },
    $route() {
      this.syncPageFromRoute();
      this.getData();
    },
  },
  computed: {
    ...mapState("DotsPatient", ["datas", "pageCount"]),
    ...mapState("Loading", ["loading"]),
    ...mapState("Admin", ["admin"]),
  },
};
</script>

<style scoped>
.toolbar-custom {
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-bottom: 2px solid #aa00ff;
  padding: 12px 16px;
  height: auto !important;
  min-height: unset !important;
}

.toolbar-custom ::v-deep .v-toolbar__content {
  height: auto !important;
  min-height: unset !important;
  padding: 0;
}

.toolbar-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #424242;
  display: flex;
  align-items: center;
}

.action-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.action-btn-primary {
  background: linear-gradient(135deg, #aa00ff 0%, #8e00cc 100%) !important;
  color: white !important;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(170, 0, 255, 0.3);
  transition: all 0.3s ease;
}

.action-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(170, 0, 255, 0.4) !important;
}

.action-btn-success {
  background: linear-gradient(135deg, #00c853 0%, #00a844 100%) !important;
  color: white !important;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 200, 83, 0.3);
  transition: all 0.3s ease;
}

.action-btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 200, 83, 0.4) !important;
}
</style>
